import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DropdownMenuItem,
} from "frontend-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Announcement } from "@dashboard/api/task";
import { Can, TaskAbilityContext } from "@dashboard/casl/taskAbility";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";
import { useDeleteAnnouncement } from "@dashboard/hooks/useAnnouncements";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { AddEditAnnouncementModal } from "./AddEditAnnouncementModal";
import { announcementIconMap } from "./announcementIconMap";
import { AnnouncementReactions } from "./AnnouncementReactions";

interface ViewAnnouncementModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    announcement: Announcement;
}

export function ViewAnnouncementModal({
    open,
    onOpenChange,
    announcement,
}: ViewAnnouncementModalProps) {
    const { t, i18n } = useTranslation();
    const { instanceId } = useHseInstance();
    const ability = useAbility(TaskAbilityContext);
    const deleteMutation = useDeleteAnnouncement();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const announcementSubject = subject("Announcement", {
        instanceId,
        createdById: announcement.createdById,
    });
    const canUpdate = ability.can("update", announcementSubject);
    const canDelete = ability.can("delete", announcementSubject);

    const iconEntry = announcementIconMap[announcement.icon];
    const authorName = `${announcement.createdBy.firstName} ${announcement.createdBy.lastName}`;
    const formattedDate = new Date(announcement.createdAt).toLocaleDateString(
        i18n.language === "nb" ? "nb-NO" : "en-US",
        { day: "numeric", month: "long", year: "numeric" },
    );

    const handleDelete = async () => {
        await deleteMutation.mutateAsync(announcement.id);
        onOpenChange(false);
    };

    return (
        <>
            <Dialog open={open && !showEdit} onOpenChange={onOpenChange}>
                <DialogContent
                    className="max-w-md gap-0 p-0"
                    menuLabel={t("common.openMenu")}
                    menu={
                        canUpdate || canDelete ? (
                            <>
                                <Can I="update" this={announcementSubject}>
                                    <DropdownMenuItem onClick={() => setShowEdit(true)}>
                                        <FontAwesomeIcon icon={faPencil} />
                                        {t("common.edit")}
                                    </DropdownMenuItem>
                                </Can>
                                <Can I="delete" this={announcementSubject}>
                                    <DropdownMenuItem
                                        variant="destructive"
                                        disabled={deleteMutation.isPending}
                                        onClick={() => setShowDeleteConfirm(true)}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                        {t("common.delete")}
                                    </DropdownMenuItem>
                                </Can>
                            </>
                        ) : undefined
                    }
                >
                    <DialogHeader className="min-w-0 flex-row items-start gap-3 py-5 pl-5 pr-24 border-b border-compendia-border">
                        <div className="flex min-w-0 items-start gap-3">
                            <div className="flex size-10 shrink-0 items-center justify-center rounded-[3px] border border-compendia-border bg-compendia-greige-40 text-compendia-muted">
                                <FontAwesomeIcon icon={iconEntry.icon} className="size-5" />
                            </div>
                            <div className="flex min-w-0 flex-col gap-1">
                                <DialogTitle className="text-body-lg font-semibold text-compendia-foreground hyphens-auto wrap-break-word whitespace-pre-wrap">
                                    {announcement.title}
                                </DialogTitle>
                                <p className="text-[12px] text-compendia-muted">
                                    {formattedDate} · {authorName}
                                </p>
                            </div>
                        </div>
                    </DialogHeader>
                    {announcement.content && (
                        <div className="min-w-0 px-5 py-4">
                            <p className="text-body-sm leading-loose whitespace-pre-wrap wrap-break-word text-compendia-gray-80">
                                {announcement.content}
                            </p>
                        </div>
                    )}

                    <AnnouncementReactions
                        id={announcement.id}
                        reactionCounts={announcement.reactionCounts}
                        currentUserReaction={announcement.currentUserReaction}
                    />

                    <DialogFooter className="flex items-center justify-end gap-2 p-5">
                        <Button variant="fill" type="button" onClick={() => onOpenChange(false)}>
                            {t("common.close")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDeleteDialog
                open={showDeleteConfirm}
                onOpenChange={setShowDeleteConfirm}
                onConfirm={handleDelete}
                title={t("announcement.deleteAnnouncement")}
                description={t("announcement.confirmDelete")}
            />

            {showEdit && (
                <AddEditAnnouncementModal
                    open={showEdit}
                    onOpenChange={setShowEdit}
                    announcement={announcement}
                />
            )}
        </>
    );
}
